import { Fragment, useEffect, useMemo, useState } from 'react';

import { Family, User, useShowFamily } from '@givebacks/api/memberhub';
import { useShowCauseW_Selector } from 'core/Api';
import { ClickableOptionsCard } from 'core/Components';
import { phoneNumberDisplayFormat, toTitleCase } from 'core/Utils';
import { useParams } from 'react-router-dom';
import {
  Avatar,
  Divider,
  KebabMenu,
  Modal,
  ModalProps,
  NextPageLoader,
  Stack,
  notifications,
  pipeSeparator,
} from 'ui';
import { useClipboard } from 'ui/Hooks';
import { IconMail, IconMobile, IconUsers } from 'ui/Icons';

interface DirectoryListItemsProps {
  items: Family[] | User[];
  itemsType: 'family' | 'user';
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  showUserRoles?: boolean;
}

const FamilyListItem = ({ family }: { family: Family }) => {
  const [isFamilyModalOpen, setIsFamilyModalOpen] = useState<string | undefined>();

  const users = useMemo(
    () =>
      family.users.filter(
        (user) =>
          user.first_name !== '' && user.first_name !== null && user.first_name !== undefined
      ),
    [family.users]
  );
  return (
    <>
      <ClickableOptionsCard.Row
        key={family.uuid}
        title={family.name}
        subtitle={users.map((user) => user.first_name).join(', ')}
        leftSection={
          <Avatar size={48} color="cobalt">
            <IconUsers />
          </Avatar>
        }
        onClick={() => {
          setIsFamilyModalOpen(family.uuid);
        }}
      />
      <FamilyModal
        opened={isFamilyModalOpen === family.uuid}
        onClose={() => setIsFamilyModalOpen(undefined)}
        familyId={family.uuid}
      />
    </>
  );
};

const UserListItem = ({ user, showUserRoles = false }: { user: User; showUserRoles?: boolean }) => {
  const clipboard = useClipboard();

  useEffect(() => {
    if (clipboard.copied) {
      notifications.show({
        variant: 'success',
        message: 'Copied to clipboard',
      });
    }
  }, [clipboard.copied]);
  return (
    <ClickableOptionsCard.Row
      key={user.uuid}
      title={`${user.first_name} ${user.last_name}`}
      subtitle={[
        showUserRoles ? user.roles.map((role) => toTitleCase(role.name)).join(', ') : null,
        user.email,
        user.phone_number ? phoneNumberDisplayFormat(user.phone_number) : null,
      ]
        .filter((val) => val)
        .join(pipeSeparator)}
      leftSection={
        <Avatar size={48} color="cobalt" name={`${user.first_name} ${user.last_name}`} />
      }
      rightSection={
        <KebabMenu
          items={[
            {
              label: 'Copy Email',
              icon: <IconMail />,
              onClick: () => {
                clipboard.copy(user.email);
              },
              visible: !!user.email,
            },
            {
              label: 'Copy Phone Number',
              icon: <IconMobile />,
              onClick: () => {
                clipboard.copy(user.phone_number);
              },
              visible: !!user.phone_number,
            },
          ]}
          buttonProps={{ size: 'sm' }}
        />
      }
      isHoverStylesActive={!!user.phone_number || !!user.email}
    />
  );
};

const DirectoryListItems = ({
  items,
  itemsType,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  showUserRoles,
}: DirectoryListItemsProps) => {
  const directoryItems = useMemo(() => {
    if (itemsType === 'family') {
      const families = items as Family[];
      return families.map((family, index) => (
        <Fragment key={family.uuid}>
          <FamilyListItem family={family} />
          {index < families.length - 1 && <Divider size="sm" color="gray.0" />}
        </Fragment>
      ));
    } else {
      const users = items as User[];
      return users.map((user, index) => (
        <Fragment key={user.uuid}>
          <UserListItem user={user} showUserRoles={showUserRoles} />
          {index < users.length - 1 && <Divider size="sm" color="gray.0" />}
        </Fragment>
      ));
    }
  }, [items, itemsType, showUserRoles]);

  return (
    <Stack gap={0}>
      {directoryItems}
      {hasNextPage && (
        <NextPageLoader
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage ?? false}
          handleFetchNextPage={fetchNextPage ?? (() => {})}
        />
      )}
    </Stack>
  );
};

interface FamilyModalProps extends ModalProps {
  familyId?: string;
}

const FamilyModalContent = ({ family }: { family?: Family }) => {
  const clipboard = useClipboard();

  useEffect(() => {
    if (clipboard.copied) {
      notifications.show({
        variant: 'success',
        message: 'Copied to clipboard',
      });
    }
  }, [clipboard.copied]);

  if (!family) return null;

  return <DirectoryListItems items={family?.users} itemsType="user" showUserRoles={true} />;
};

const FamilyModal = ({ opened, onClose, familyId }: FamilyModalProps) => {
  const { uuid } = useParams();
  const { data: cause } = useShowCauseW_Selector({ uuid });
  const { data: family } = useShowFamily({
    familyId: familyId as string,
    organizationId: cause?.memberhub_id as string,
  });
  const title = useMemo(() => {
    if (!family) return '';
    if (family?.name.includes('Family')) {
      return `${family?.name}`;
    } else {
      return `${family?.name} Family`;
    }
  }, [family]);
  return (
    <Modal opened={opened} onClose={onClose} title={title}>
      <FamilyModalContent family={family} />
    </Modal>
  );
};

export { DirectoryListItems };
